import { useTranslation } from "react-i18next";
// TopUpModal — credit-pack purchase via Stripe Checkout (test mode).
// Polls /credits/checkout/status/:sid until paid (since dev webhook may not fire).
import { useEffect, useState } from "react";
import { X, Sparkles, Coins, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import api from "@/api";
import { useBodyScrollLock } from "@/lib/useBodyScrollLock";
export default function TopUpModal({
  open,
  onClose,
  onPaid,
  requiredCredits = 0
}) {
  const {
    t
  } = useTranslation();
  useBodyScrollLock(!!open);
  const [packs, setPacks] = useState([]);
  const [busy, setBusy] = useState(null);
  const [polling, setPolling] = useState(null); // session_id

  useEffect(() => {
    if (!open) return;
    api.get("/credits/packs").then(({
      data
    }) => setPacks(data.items || [])).catch(() => {});
  }, [open]);

  // After returning from Stripe, the success_url puts ?session_id=xxx — but
  // since we open Stripe in a new tab/window, we poll on this side.
  useEffect(() => {
    if (!polling) return;
    let cancelled = false;
    const poll = async () => {
      for (let i = 0; i < 60; i++) {
        if (cancelled) return;
        try {
          const {
            data
          } = await api.get(`/credits/checkout/status/${polling}`);
          if (data.payment_status === "paid") {
            toast.success(`+${data.credits_added} credits! Balance: ${data.balance}`);
            onPaid?.(data.balance);
            setPolling(null);
            onClose?.();
            return;
          }
        } catch {/* ignore */}
        await new Promise(r => setTimeout(r, 3000));
      }
      setPolling(null);
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [polling, onPaid, onClose]);
  const buy = async pack => {
    setBusy(pack.id);
    try {
      const {
        data
      } = await api.post("/credits/checkout", {
        pack_id: pack.id,
        origin_url: window.location.origin
      });
      window.open(data.url, "_blank", "noopener,noreferrer");
      setPolling(data.session_id);
      toast.info(t("topUpModal.complete_payment_in_the_new_tab_we_ll_au"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
    } finally {
      setBusy(null);
    }
  };
  if (!open) return null;
  return <div className="fixed inset-0 bg-black/70 z-[60] flex items-end sm:items-center justify-center p-4" onClick={onClose} data-testid="topup-modal">
            <div className="bg-background border border-border rounded-3xl w-full max-w-[430px] p-5 max-h-[90dvh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                <div className="flex items-start justify-between mb-3">
                    <div>
                        <p className="metric-label">{t("topUpModal.credits")}</p>
                        <h2 className="font-display text-2xl font-semibold tracking-tighter">
                            {t("topUpModal.top_up_your_balance")}
                        </h2>
                        <p className="text-xs text-muted-foreground mt-1">
                            {t("topUpModal.1_credit_0_10_besteed_aan_ai_coach_weekr")}
                        </p>
                    </div>
                    <button onClick={onClose} className="h-8 w-8 rounded-full bg-secondary flex items-center justify-center" data-testid="topup-close">
                        <X size={14} />
                    </button>
                </div>

                {requiredCredits > 0 && <div className="rounded-xl bg-[#FF9F0A]/10 border border-[#FF9F0A]/30 p-3 mb-3 flex items-start gap-2" data-testid="topup-required">
                        <Sparkles size={14} className="text-[#FF9F0A] mt-0.5" />
                        <p className="text-[11px] text-muted-foreground leading-relaxed">
                            {t("topUpModal.this_action_needs")} <span className="font-semibold text-foreground">{requiredCredits} credits</span>{t("topUpModal.pick_a_pack_below_to_continue")}
                        </p>
                    </div>}

                <div className="space-y-2.5">
                    {packs.map(p => {
          const perCredit = (p.price / p.credits).toFixed(3);
          const isBusy = busy === p.id || polling;
          return <button key={p.id} disabled={isBusy} onClick={() => buy(p)} data-testid={`topup-pack-${p.id}`} className="w-full text-left rounded-2xl border border-border bg-card p-4 active:scale-[0.99] transition disabled:opacity-50">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="h-10 w-10 rounded-xl bg-[#A855F7]/15 flex items-center justify-center">
                                            <Coins size={18} className="text-[#A855F7]" />
                                        </div>
                                        <div>
                                            <p className="font-semibold">{p.label} · {p.credits} credits</p>
                                            <p className="text-[11px] text-muted-foreground">
                                                {p.subtitle} · €{perCredit}/credit
                                            </p>
                                        </div>
                                    </div>
                                    <div className="text-right flex flex-col items-end gap-0.5">
                                        <span className="font-data text-lg font-bold tabular-nums">
                                            €{p.price.toFixed(2)}
                                        </span>
                                        {p.discount_pct > 0 && <span className="text-[9px] font-bold text-[#32D74B] bg-[#32D74B]/15 px-1.5 py-0.5 rounded-full">
                                                −{p.discount_pct}%
                                            </span>}
                                    </div>
                                </div>
                                {busy === p.id && <div className="flex items-center gap-1.5 mt-2 text-[11px] text-[#A855F7]">
                                        <Loader2 size={12} className="animate-spin" />
                                        {t("topUpModal.opening_stripe")}
                                    </div>}
                            </button>;
        })}
                </div>

                {polling && <div className="rounded-xl bg-secondary p-3 mt-3 flex items-center gap-2 text-xs" data-testid="topup-polling">
                        <Loader2 size={14} className="animate-spin" />
                        {t("topUpModal.waiting_for_payment_confirmation")}
                    </div>}

                <div className="mt-4 pt-3 border-t border-border flex items-center gap-1.5 text-[10px] text-muted-foreground">
                    <Check size={11} className="text-[#32D74B]" />
                    {t("topUpModal.test_mode_use_stripe_test_card_4242_4242")}
                </div>
            </div>
        </div>;
}